import {
  calendarDateFromInstant,
  formatCalendarDate,
  parseCalendarDate,
} from './calendarDate.js';

function returnsAtMillis(returnsAt) {
  if (typeof returnsAt === 'number' && Number.isFinite(returnsAt)) {
    return returnsAt;
  }
  if (typeof returnsAt === 'string' && returnsAt) {
    const parsed = Date.parse(returnsAt);
    return Number.isNaN(parsed) ? NaN : parsed;
  }
  return NaN;
}

/**
 * True when the user has declared themselves out and any return time is still ahead.
 * A missing status, or a return time that has passed, is in office.
 *
 * @param {{ outOfOffice?: { returnsAt?: string|number|null }|null }|null|undefined} user
 * @param {number} [now]
 * @returns {boolean}
 */
export function isOutOfOffice(user, now = Date.now()) {
  const outOfOffice = user?.outOfOffice;
  if (!outOfOffice) {
    return false;
  }
  if (outOfOffice.returnsAt == null) {
    return true;
  }
  const returnsAt = returnsAtMillis(outOfOffice.returnsAt);
  if (Number.isNaN(returnsAt)) {
    return true;
  }
  return now < returnsAt;
}

/**
 * The stored status when the user is currently out, otherwise null.
 * @param {string|null|undefined} assigneeId
 * @param {Array<{ userId?: string, outOfOffice?: { returnsAt?: string|number|null }|null }>} [users]
 * @param {number} [now]
 * @returns {{ returnsAt: string|number|null }|null}
 */
export function outOfOfficeForAssignee(assigneeId, users = [], now = Date.now()) {
  if (!assigneeId) {
    return null;
  }
  const user = users.find((entry) => entry.userId === assigneeId);
  if (!user || !isOutOfOffice(user, now)) {
    return null;
  }
  return user.outOfOffice;
}

function formatReturnDay(returnsAt) {
  return new Date(returnsAtMillis(returnsAt)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

/**
 * Closed-cell label: "In office", "Out of office", or "Out until 2 Nov".
 * @param {{ returnsAt?: string|number|null }|null|undefined} outOfOffice
 * @param {number} [now]
 * @returns {string}
 */
export function availabilityLabel(outOfOffice, now = Date.now()) {
  if (!isOutOfOffice({ outOfOffice }, now)) {
    return 'In office';
  }
  if (outOfOffice?.returnsAt == null) {
    return 'Out of office';
  }
  return `Out until ${formatReturnDay(outOfOffice.returnsAt)}`;
}

/**
 * Tooltip beside an assignee: "Out of office" or "Out of office until 2 Nov".
 * @param {{ returnsAt?: string|number|null }|null|undefined} outOfOffice
 * @param {number} [now]
 * @returns {string}
 */
export function outOfOfficeTooltip(outOfOffice, now = Date.now()) {
  if (!isOutOfOffice({ outOfOffice }, now)) {
    return '';
  }
  if (outOfOffice?.returnsAt == null) {
    return 'Out of office';
  }
  return `Out of office until ${formatReturnDay(outOfOffice.returnsAt)}`;
}

/** Local midnight of the calendar day after today, as yyyy-MM-dd. */
export function tomorrowCalendarDate() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(0, 0, 0, 0);
  return formatCalendarDate(date);
}

/** ISO-8601 instant for local midnight of a yyyy-MM-dd calendar date. */
export function localMidnightIso(calendarDate) {
  const date = parseCalendarDate(calendarDate);
  if (!date) {
    return null;
  }
  return date.toISOString();
}

/**
 * @param {{ returnsAt?: string|number|null }|null|undefined} outOfOffice
 * @param {number} [now]
 * @returns {{ away: boolean, mode: 'indefinite'|'until', date: string }}
 */
export function draftFromOutOfOffice(outOfOffice, now = Date.now()) {
  if (!isOutOfOffice({ outOfOffice }, now)) {
    return { away: false, mode: 'indefinite', date: '' };
  }
  if (outOfOffice?.returnsAt == null) {
    return { away: true, mode: 'indefinite', date: '' };
  }
  return {
    away: true,
    mode: 'until',
    date: calendarDateFromInstant(outOfOffice.returnsAt),
  };
}

/**
 * @param {{ away: boolean, mode: 'indefinite'|'until', date: string }} draft
 * @returns {{ markOutOfOffice: boolean, returnsAt: string|null }}
 */
export function payloadFromDraft(draft) {
  if (!draft.away) {
    return { markOutOfOffice: false, returnsAt: null };
  }
  if (draft.mode === 'until') {
    return { markOutOfOffice: true, returnsAt: localMidnightIso(draft.date) };
  }
  return { markOutOfOffice: true, returnsAt: null };
}

function formatNameList(names) {
  if (names.length <= 1) {
    return names[0] || '';
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * Documents whose assignee is a user currently out of office.
 * @param {Array<{ assigneeId?: string|null }>} [documents]
 * @param {Array<{ userId?: string, name?: string, outOfOffice?: object|null }>} [users]
 * @param {number} [now]
 * @returns {{ count: number, names: string[] }}
 */
export function outOfOfficeAssignmentSummary(documents = [], users = [], now = Date.now()) {
  const names = new Map();
  let count = 0;
  documents.forEach((doc) => {
    const user = users.find((entry) => entry.userId === doc.assigneeId);
    if (!user || !isOutOfOffice(user, now)) {
      return;
    }
    count += 1;
    names.set(user.userId, user.name);
  });
  return { count, names: [...names.values()] };
}

/**
 * @param {{ count: number, names: string[] }} summary
 * @returns {string}
 */
export function outOfOfficeAssignmentWarning(summary) {
  if (!summary || summary.count === 0) {
    return '';
  }
  const documents = summary.count === 1
    ? '1 document is'
    : `${summary.count} documents are`;
  const who = formatNameList(summary.names);
  const verb = summary.names.length === 1 ? 'is' : 'are';
  return `${documents} assigned to ${who}, who ${verb} currently out of office`;
}
